import React, { useEffect, useRef } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Stack,
} from '@mui/material';
import { CheckCircle, Error as ErrorIcon, Login } from '@mui/icons-material';
import { useIntl } from 'react-intl';
import { useCustomerAuth } from '../CustomerAuthContext';
import { useActivateClientMutation } from '../queries';
import QRCodeScanner from '@shared/components/QrCodeScanner';
import Browsers from '/public/assets/images/browsers.png';

export const isInAppBrowser = (): boolean => {
  const ua = navigator.userAgent || navigator.vendor;
  return ua.indexOf('FBAN') > -1 || ua.indexOf('FBAV') > -1 || ua.indexOf('Instagram') > -1;
};

export const ActivationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const { login, isAuthenticated, hasPinSet, userId: authUserId } = useCustomerAuth();

  const {
    mutate: activate,
    isPending: loading,
    isSuccess: success,
    error,
  } = useActivateClientMutation();

  const hasActivatedRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated) {
      return;
    }

    const token = searchParams.get('token');

    if (!token) {
      return;
    }

    if (isInAppBrowser()) {
      navigate(`/customer-reservations/browser-guide?token=${token}`, { replace: true });
      return;
    }

    if (hasActivatedRef.current) {
      return;
    }
    hasActivatedRef.current = true;

    activate(token, {
      onSuccess: async response => {
        if (response.success && response.sessionToken) {
          await login();
          // navigation is handled by the isAuthenticated guard below
        }
      },
    });
  }, []);

  const token = searchParams.get('token');

  if (isAuthenticated) {
    // Redirect to the new merged booking page using the hairdresser's userId from session
    if (authUserId) {
      return <Navigate to={`/public-reservation/profile/${authUserId}`} replace />;
    }
    // Fallback if userId not yet loaded
    return (
      <Navigate
        to={hasPinSet ? '/customer-reservations/booking' : '/customer-reservations/set-pin'}
        replace
      />
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8, padding: 3 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Stack spacing={3} alignItems="center">
          {loading && (
            <>
              <CircularProgress size={60} />
              <Typography variant="h5" gutterBottom>
                {intl.formatMessage({ id: 'customerAuth.activating' })}
              </Typography>
              <Typography variant="body1" color="text.secondary" textAlign="center">
                {intl.formatMessage({ id: 'customerAuth.activatingMessage' })}
              </Typography>
            </>
          )}

          {success && (
            <>
              <CheckCircle color="success" sx={{ fontSize: 80 }} />
              <Typography variant="h5" gutterBottom>
                {intl.formatMessage({ id: 'customerAuth.activationSuccess' })}
              </Typography>
              <Typography variant="body1" color="text.secondary" textAlign="center">
                {intl.formatMessage({ id: 'customerAuth.activationSuccessMessage' })}
              </Typography>
            </>
          )}

          {error && !success && (
            <>
              <ErrorIcon color="error" sx={{ fontSize: 80 }} />
              <Typography variant="h5" gutterBottom>
                {intl.formatMessage({ id: 'customerAuth.activationFailed' })}
              </Typography>
              <Alert severity="error" sx={{ width: '100%' }}>
                {error.message ||
                  intl.formatMessage({ id: 'customerAuth.activationFailedMessage' })}
              </Alert>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {intl.formatMessage({ id: 'customerAuth.contactHairdresser' })}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/customer-reservations')}
                sx={{ mt: 2 }}
              >
                {intl.formatMessage({ id: 'customerAuth.backToHome' })}
              </Button>
            </>
          )}

          {!loading && !success && !error && !token && (
            <>
              <ErrorIcon color="info" sx={{ fontSize: 80 }} />
              <Typography variant="h6" gutterBottom>
                {intl.formatMessage({
                  id: 'customerAuth.missingToken',
                  defaultMessage: 'Nemáte aktivovaný přístup, učiňte tak u svého kadeřníka.',
                })}
              </Typography>
              <Typography variant="body2" gutterBottom color="text.secondary" textAlign="center">
                {intl.formatMessage({
                  id: 'customerAuth.missingTokenHint',
                  defaultMessage:
                    'Pokud tak očekáváte, ověřte si, že otevíráte stejný prohlížeč, ve kterém jste se poprvé přihlásil/la. Pokud došlo ke smazání dat prohlížeče, byli jste odhlášeni z důvodu bezpečnosti.',
                })}
              </Typography>
              <img
                src={Browsers}
                style={{
                  maxWidth: '30%',
                  objectFit: 'contain',
                }}
              />
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<Login />}
                  onClick={() => navigate('/customer-reservations/login')}
                >
                  {intl.formatMessage({ id: 'customerAuth.loginButton' })}
                </Button>
                <QRCodeScanner />
              </Stack>
            </>
          )}
        </Stack>
      </Paper>
    </Container>
  );
};
